import React, { Suspense, useEffect } from "react";
import { Provider } from "react-redux";
import { captureUtmParams } from "src/utils/utmTracker";
import { ApolloProvider } from "@apollo/client";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { Routes, Route, Navigate, useNavigate, Outlet } from "react-router-dom";
import { FeatureProvider } from "src/context/global";
import theme from "src/theme";
import BrandedLoader from "src/components/shared/atoms/BrandedLoader";
import { TourProvider } from "src/context/global/TourContext";
import { ContextProvider } from "src/context/chats";
import { WebRTCProvider } from "src/context/chats/WebRTCContext";
import Login from "src/pages/auth/login/Login";
import Signup from "src/pages/auth/signup/Signup";
// import ManageFailedPayment from "src/pages/ManageFailedPayment";
import PricingPlans from "src/pages/PricingPlans";
import InstagramCallback from "src/pages/auth/LoginWithIG";
//
import AuthForgetPassword from "src/pages/auth/ForgetPassword";
import Facebook from "src/components/pages/settings/rightPanel/channels/facebook";
import Whatsapp from "src/components/pages/settings/rightPanel/channels/whatsapp";
import CannedResponses from "src/components/pages/settings/rightPanel/canned";
import Billing from "src/components/pages/settings/rightPanel/Billing";
import NotSelected from "src/components/pages/settings/rightPanel/notSelected";
import Twilio from "src/components/pages/settings/rightPanel/channels/twilio";
import Instagram from "src/components/pages/settings/rightPanel/channels/instagram";
import Label from "src/components/pages/settings/rightPanel/labels";
import UpgradePlan from "src/components/pages/settings/rightPanel/UpgradePlan";
import DynamicTitleWrapper from "src/components/hoc/dynamicTitleWrapper";
// import Features from "src/components/pages/settings/rightPanel/Features";
// import ProductAndPricing from "src/components/pages/settings/rightPanel/ProductAndPricings";
import Dashboard from "src/pages/dashboard";
import SettingsRouteGuard from "src/components/hoc/SettingsRouteGuard";
import ProfileSettings from "src/components/pages/settings/rightPanel/profileSetting";
import ChatbotWidgets from "src/components/pages/settings/rightPanel/chatbot";
import WidgetEditor from "src/components/pages/settings/rightPanel/chatbot/WidgetEditor";
import WidgetChat from "src/components/pages/chatbot/WidgetChat";
import { BrowserCallAgentProvider } from "src/context/browserCall/BrowserCallAgentContext";
import IncomingBrowserCallToast from "src/components/pages/browserCall/IncomingBrowserCallToast";
import AgentCallPopout from "src/components/pages/browserCall/AgentCallPopout";
import GuestCallRoom from "src/components/pages/browserCall/GuestCallRoom";
import { TwilioVoiceProvider } from "src/context/twilioVoice/TwilioVoiceContext";
import ActiveCallWidget from "src/components/pages/twilioVoice/ActiveCallWidget";
import IncomingTwilioCallToast from "src/components/pages/twilioVoice/IncomingTwilioCallToast";
import { MessengerCallProvider } from "src/context/messengerCall/MessengerCallContext";
import IncomingMessengerCallToast from "src/components/pages/messengerCall/IncomingMessengerCallToast";
import MessengerActiveCallWidget from "src/components/pages/messengerCall/MessengerActiveCallWidget";
import ErrorBoundary from "./components/shared/atoms/ErrorBoundary";
import apolloConfig from "./config/apolloupdate";
import Layout from "./layout/MainLayout";
import AuthGuard from "./components/hoc/AuthGuard";
import GuestGuard from "./components/hoc/GuestGuard";
// import Privacy from "./pages/auth/privacy";
import DocumentViewer from "./components/pages/auth/shared/DocumentViewer";
import AuthGuardForSubscription from "./components/hoc/AuthGuardForSubscription";
import ActiveCallWindow from "./components/pages/chat/ActiveCallWindow";
import { store } from "./store/store";
import "./App.css";

const AuthEmailConfirm = React.lazy(
  () => import("src/pages/auth/EmailConfirm"),
);
const InviteUser = React.lazy(() => import("src/pages/auth/InviteUser"));
const ResetPassword = React.lazy(() => import("src/pages/auth/resetPassword"));
const UpdateBillingUpdates = React.lazy(
  () => import("src/pages/auth/updateBillingDetails"),
);

const Users = React.lazy(() => import("src/pages/users"));
const Reports = React.lazy(() => import("src/pages/reports"));
const Chat = React.lazy(() => import("src/pages/chat"));
const CallLogs = React.lazy(() => import("src/pages/callLogs"));
const Setting = React.lazy(() => import("src/pages/settings"));

const AppRouterContent = ({ apolloClient }: { apolloClient: any }) => {
  const navigate = useNavigate();

  // 🔄 Auto-Sync data when internet connection is restored
  useEffect(() => {
    const handleOnline = () => {
      console.log("Internet restored. Syncing active queries...");
      // Refetch all queries that are currently active on the screen
      apolloClient.refetchQueries({
        include: "active",
      });
    };

    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [apolloClient]);

  return (
    <ErrorBoundary onReset={() => navigate("/")}>
      <ApolloProvider client={apolloClient}>
        <Provider store={store}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <DynamicTitleWrapper />
            <Suspense fallback={<BrandedLoader />}>
              <Routes>
                {/* Layout-protected routes */}
                <Route path="/" element={<AuthGuard />}>
                  <Route
                    element={
                      <FeatureProvider>
                        <TourProvider>
                          <ContextProvider>
                            <WebRTCProvider>
                              <Outlet />
                            </WebRTCProvider>
                          </ContextProvider>
                        </TourProvider>
                      </FeatureProvider>
                    }
                  >
                    <Route
                      element={
                        <BrowserCallAgentProvider>
                          <TwilioVoiceProvider>
                            <MessengerCallProvider>
                              <Layout />
                              <IncomingBrowserCallToast />
                              <IncomingTwilioCallToast />
                              <ActiveCallWidget />
                              <IncomingMessengerCallToast />
                              <MessengerActiveCallWidget />
                            </MessengerCallProvider>
                          </TwilioVoiceProvider>
                        </BrowserCallAgentProvider>
                      }
                    >
                      <Route index element={<Dashboard />} />
                      <Route path="users" element={<Users />} />
                      <Route path="chats" element={<Chat />} />
                      <Route path="reports" element={<Reports />} />
                      <Route path="call-logs" element={<CallLogs />} />
                      <Route path="settings" element={<Setting />}>
                        <Route index element={<NotSelected />} />
                        <Route
                          path="facebook"
                          element={
                            <SettingsRouteGuard>
                              <Facebook />
                            </SettingsRouteGuard>
                          }
                        />
                        <Route
                          path="instagram"
                          element={
                            <SettingsRouteGuard>
                              <Instagram />
                            </SettingsRouteGuard>
                          }
                        />
                        <Route
                          path="whatsapp"
                          element={
                            <SettingsRouteGuard>
                              <Whatsapp />
                            </SettingsRouteGuard>
                          }
                        />
                        <Route
                          path="twilio"
                          element={
                            <SettingsRouteGuard>
                              <Twilio />
                            </SettingsRouteGuard>
                          }
                        />
                        <Route path="label" element={<Label />} />
                        <Route path="upgrade-plan" element={<UpgradePlan />} />
                        <Route path="billing" element={<Billing />} />
                        <Route
                          path="canned-response"
                          element={<CannedResponses />}
                        />
                        <Route path="chatbot-widgets">
                          <Route index element={<ChatbotWidgets />} />
                          <Route path=":widgetId" element={<WidgetEditor />} />
                        </Route>
                        <Route path="profile" element={<ProfileSettings />} />
                        <Route path="*" element={<Navigate to="/settings" />} />
                      </Route>
                    </Route>
                    <Route
                      path="whatsapp-call-active"
                      element={<ActiveCallWindow />}
                    />
                    <Route
                      path="browser-call-active"
                      element={<AgentCallPopout />}
                    />
                  </Route>
                </Route>

                <Route
                  path="privacy-policy"
                  element={
                    <DocumentViewer
                      fileName="Privacy Policy.html"
                      title="Privacy Policy"
                    />
                  }
                />
                <Route
                  path="terms-of-use"
                  element={
                    <DocumentViewer
                      fileName="Terms of Use.html"
                      title="Terms of Use"
                    />
                  }
                />
                <Route element={<AuthGuardForSubscription />}>
                  <Route path="subscription" element={<PricingPlans />} />
                </Route>

                <Route
                  path="/auth/instagram/callback"
                  element={<InstagramCallback />}
                />

                {/* Standalone Chatbot Widget */}
                <Route
                  path="/chatbot-widget/:deploymentId"
                  element={<WidgetChat />}
                />

                {/* Public browser-to-browser call room (no auth) */}
                <Route path="/call/:roomId" element={<GuestCallRoom />} />
                {/* Guest routes (not wrapped in Layout) */}
                <Route element={<GuestGuard />}>
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route
                    path="/forgetpassword"
                    element={<AuthForgetPassword />}
                  />
                  <Route path="/confirm" element={<AuthEmailConfirm />} />
                  <Route path="/join" element={<InviteUser />} />
                  <Route
                    path="/resetpassword/:token/:email"
                    element={<ResetPassword />}
                  />
                </Route>

                <Route element={<GuestGuard isbillingGuard />}>
                  <Route
                    path="/updatebillingdetails/:token"
                    element={<UpdateBillingUpdates />}
                  />
                </Route>

                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Suspense>
          </ThemeProvider>
        </Provider>
      </ApolloProvider>
    </ErrorBoundary>
  );
};

const App = () => {
  useEffect(() => {
    captureUtmParams();
  }, []);

  // 🔄 Stable refetch function to be used by both window events and WebSocket
  const handleSync = () => {
    const { apolloClient } = apolloConfig() as any;
    if (apolloClient) {
      console.log("Global sync triggered...");
      apolloClient.refetchQueries({
        include: "active",
      });
    }
  };

  const { apolloClient } = apolloConfig(handleSync) as any;

  return <AppRouterContent apolloClient={apolloClient} />;
};

export default App;
